// The furniture a tractor beam moves, in the browser (shared/furniture.ts has the rules; the server keeps where each piece is,
// server/furniture.ts). Each piece the ship has told us about is the ship's own meshes, put where it is now: their world matrices
// set to where they were built, turned about the piece's middle and slid. It glides to each new place the server sends while a
// beam holds it (not solid then, so it never shoves anyone), and once it's down its collision is built again where it stands.
// The seats in it go with it, and so do the station screens of a seat that is a station.
import { Matrix, Vector3, type AbstractMesh } from "@babylonjs/core";
import type { Vec3 } from "../shared/ship.js";
import { FURN_GONE_DROP, placePoint, samePlace, type FurnPlace, type FurnPlaced } from "../shared/furniture.js";

interface SeatLike { id: string; pos: Vector3; yaw: number | null }
export interface FurnDeps {
  meshes(name: string): AbstractMesh[];                  // the ship's meshes of this name (a multi-material one comes in parts)
  seats(): SeatLike[];                                    // the ship's seats (moved in place)
  held(id: string): boolean;                              // a beam has hold of this piece
  solid(names: string[], on: boolean): void;              // its collision: taken away while it moves, built again where it stands
  station(seat: string, move: (v: Vector3) => Vector3, dyaw: number): void;   // a station's screens follow its seat
}
interface Piece { id: string; c: Vec3; names: string[]; meshes: AbstractMesh[]; base: Matrix[]; cur: FurnPlace; want: FurnPlace; seats: { s: SeatLike; pos: Vector3; yaw: number | null }[]; solid: boolean }
const angle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
export class FurnitureView {
  private pieces = new Map<string, Piece>();
  private pending: FurnPlaced[] = []; private ready = false;
  constructor(private d: FurnDeps) {}
  // the ship is built (meshes frozen where they were made, seats found, screens up): what came before now goes in
  start() { this.ready = true; const p = this.pending; this.pending = []; if (p.length) this.onList(p); }
  onList(list: FurnPlaced[]) {
    if (!this.ready) { this.pending.push(...list); return; }
    for (const f of list) {
      const want: FurnPlace = { dx: f.dx, dz: f.dz, yaw: f.yaw, ...(f.gone ? { gone: true } : {}) }; let p = this.pieces.get(f.id);
      if (!p) { const made = this.make(f, want); if (!made) continue; p = made; this.pieces.set(f.id, p); this.apply(p); continue; }   // (first heard of: straight there)
      p.want = want;
    }
  }
  private make(f: FurnPlaced, at: FurnPlace): Piece | null {
    const meshes = f.meshes.flatMap((n) => this.d.meshes(n)); if (!meshes.length) return null;
    const names = new Set(f.meshes);
    const seats = this.d.seats().filter((s) => names.has(s.id) || names.has(s.id.replace(/_Seat$/, ""))).map((s) => ({ s, pos: s.pos.clone(), yaw: s.yaw }));
    return { id: f.id, c: f.c, names: f.meshes, meshes, base: meshes.map((m) => m.getWorldMatrix().clone()), cur: { ...at }, want: { ...at }, seats, solid: samePlace(at, { dx: 0, dz: 0, yaw: 0 }) };
  }
  private apply(p: Piece) {
    const { c, cur } = p, drop = cur.gone ? FURN_GONE_DROP : 0, T = Matrix.Translation(-c.x, 0, -c.z).multiply(Matrix.RotationY(cur.yaw)).multiply(Matrix.Translation(c.x + cur.dx, -drop, c.z + cur.dz));
    p.meshes.forEach((m, i) => m.freezeWorldMatrix(p.base[i].multiply(T)));
    const move = (v: Vector3) => { const q = placePoint(v, c, cur); return new Vector3(q.x, q.y, q.z); };
    for (const e of p.seats) { const q = move(e.pos); e.s.pos.x = q.x; e.s.pos.y = q.y; e.s.pos.z = q.z; e.s.yaw = e.yaw === null ? null : e.yaw + cur.yaw; this.d.station(e.s.id, move, cur.yaw); }
  }
  // every frame: glide toward where the server says; solid again once it's down
  tick(dt: number) {
    const k = 1 - Math.exp(-dt * 12);
    for (const p of this.pieces.values()) {
      const held = this.d.held(p.id);
      if (held && p.solid) { this.d.solid(p.names, false); p.solid = false; }
      if (!samePlace(p.cur, p.want)) {
        const dy = angle(p.want.yaw - p.cur.yaw), far = Math.hypot(p.want.dx - p.cur.dx, p.want.dz - p.cur.dz) > 6 || !!p.want.gone !== !!p.cur.gone;   // (taken out, or brought back: at once)
        if (far || (Math.hypot(p.want.dx - p.cur.dx, p.want.dz - p.cur.dz) < 0.002 && Math.abs(dy) < 0.002)) p.cur = { ...p.want };
        else p.cur = { dx: p.cur.dx + (p.want.dx - p.cur.dx) * k, dz: p.cur.dz + (p.want.dz - p.cur.dz) * k, yaw: p.cur.yaw + dy * k };
        this.apply(p);
      } else if (!held && !p.solid) { this.d.solid(p.names, true); p.solid = true; }
    }
  }
  // for tests (window.__chair)
  state() { return [...this.pieces.values()].map((p) => ({ id: p.id, cur: p.cur, want: p.want, solid: p.solid, seats: p.seats.length })); }
}
